import React, { useCallback } from 'react'
import { State, useState } from '@hookstate/core'
import { Col, Row } from 'react-bootstrap'
import { Branding } from '../../branding/branding'
import { useLanguageState } from '../../globalStates/LanguageState'
import { ReferenceDataType, withReferenceDataState } from '../../globalStates/ReferenceDataHOC'
import { EventDate, useExhibitorDataState } from '../../globalStates/ExhibitorDataState'
import { useConfigDataState } from '../../globalStates/ConfigDataState'
import { getElementFieldValue, hasErrorsAll, makeCheckHandler, makeSetHandlerPlain, makeValidationHandler } from '../../globalStates/utils'
import { fieldNameByLang, getElementFieldValueWithLanguage, makeSetHandlerWithLanguage, useLanguageSwitch } from '../../utils/Language'
import { formatDateY2d, formatTimeIntervalHm } from '../../utils/Dates'
import TextField from '../../components/Field/Text'
import TextAreaField from '../../components/Field/TextArea'
import ToggleSwitchField from '../../components/Field/ToggleSwitchField'
import FilePicker from '../../components/FilePicker'
import PageContents from '../../components/PageContents'
import Section from '../../components/Section'
import { Tabs, Tab } from '../../components/Tabs'
import KeywordContents from '../profile/KeywordContents'
import MultimediaContents from '../multimedia/MutimediaContents'
import PersonsSection from '../persons/PersonsSection'
import LanguageSwitch from '../languageSwitch'
import PrerecordedContents from './PrerecordedContents'
import { hasServiceKeys } from '../../globalStates/ConfigDataState'

const EVENT_DATE_TYPE_PRERECORDED = 'Prerecorded'

function mapListenOn(isListable: boolean, branding: Branding) {
    return isListable ? branding.configuration.eventDatesPage.sctEventDates.lblListenonTRUE : branding.configuration.eventDatesPage.sctEventDates.lblListenonFALSE
}

interface EventDateSectionProps {
    eventDateState: State<EventDate>
    index: number
}

const EventDatesSection: React.FC<EventDateSectionProps & ReferenceDataType> = ({ eventDateState, refDataState, index }) => {
    const branding = useLanguageState().getStrings()
    const exiState = useExhibitorDataState()
    const eventDate = useState(eventDateState)
    const isMonoLingual = branding.configuration.monoLingual ? 'De' : 'En'
    const { currentLanguage, onChangeLanguageHandler } = useLanguageSwitch(isMonoLingual);
    const serviceKeys = useConfigDataState().getServiceKeys()
    const keywordsVisible = hasServiceKeys(serviceKeys.keywords)

    const onChangeLogo = useCallback((image: File, onUploadProgress, onSuccessCallback, onErrorCallback) => {
        exiState.upload("LOGO", image, onUploadProgress)
            .then(response => {
                onSuccessCallback();
                if (response) {
                    eventDate.logoFileId.set(response.fileId)
                    eventDate.logourl.set(response.fileUrl)
                }
            })
            .catch(reason => onErrorCallback(reason))
    }, [exiState, eventDate])

    const onRemoveLogo = useCallback(() => {
        eventDate.logoFileId.set('')
        eventDate.logourl.set('')
    }, [eventDate])

    return (
        <Section
            title={eventDate.name.get() || eventDate.nameDe.get() || branding.configuration.eventDatesPage.sctEventDates.titleEmptyEventDate}
            collapsible
            entityState={eventDate}>
            <Row>
                <Col xs={3}>
                    <FilePicker
                        name="companyLogo"
                        label={branding.configuration.eventDatesPage.sctEventDates.lblEventDateImage}
                        message={branding.configuration.eventDatesPage.sctEventDates.lblEventDateImageMessage}
                        browseLabel={branding.configuration.eventDatesPage.sctEventDates.btnBrowseFiles}
                        deleteLabel={branding.configuration.eventDatesPage.sctEventDates.btnDeleteImg}
                        placeholderTitle={branding.configuration.eventDatesPage.sctEventDates.hntTitle}
                        placeholderText={branding.configuration.eventDatesPage.sctEventDates.hntProp}
                        value={getElementFieldValueWithLanguage(eventDate, "logourl", currentLanguage)}
                        onChangeImage={onChangeLogo}
                        onRemoveImage={onRemoveLogo}
                    />
                </Col>
                <Col xs={9}>
                    <Row>
                        <Col>
                            <LanguageSwitch
                                id={`eventDate_${index}_language_switch`}
                                name={`eventDate_${index}_languages`}
                                value={currentLanguage}
                                onChange={onChangeLanguageHandler}
                                entityState={eventDate}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextField
                                name={fieldNameByLang("name", currentLanguage)}
                                label={branding.configuration.eventDatesPage.sctEventDates.lblEventdateTitle}
                                placeholder={branding.configuration.eventDatesPage.sctEventDates.hntEventdateTitle}
                                validate={makeValidationHandler(eventDate, 'name')}
                                value={getElementFieldValueWithLanguage(eventDate, "name", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(eventDate, 'name', currentLanguage)}
                                required
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={4}>
                            <TextField
                                name={fieldNameByLang("date", currentLanguage)}
                                label={branding.configuration.eventDatesPage.sctEventDates.lblDate}
                                placeholder={branding.configuration.eventDatesPage.sctEventDates.hntDate}
                                value={formatDateY2d(eventDate.startdatetime.get())}
                                readonly={true}
                            />
                        </Col>
                        <Col xs={4}>
                            <TextField
                                name={fieldNameByLang("time", currentLanguage)}
                                label={branding.configuration.eventDatesPage.sctEventDates.lblTime}
                                placeholder={branding.configuration.eventDatesPage.sctEventDates.hntTime}
                                value={formatTimeIntervalHm(eventDate.startdatetime.get(), eventDate.enddatetime.get())}
                                readonly={true}
                            />
                        </Col>
                        <Col xs={4}>
                            <TextField
                                name={fieldNameByLang("location", currentLanguage)}
                                label={branding.configuration.eventDatesPage.sctEventDates.lblLocation}
                                placeholder={branding.configuration.eventDatesPage.sctEventDates.hntLocation}
                                value={getElementFieldValueWithLanguage(eventDate, "location", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(eventDate, 'location', currentLanguage)}
                                readonly={true}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6}>
                            <TextField
                                name={fieldNameByLang("eventType", currentLanguage)}
                                label={branding.configuration.eventDatesPage.sctEventDates.lblEventType}
                                placeholder={branding.configuration.eventDatesPage.sctEventDates.hntEventType}
                                value={getElementFieldValue(eventDate, "type")}
                                onChange={makeSetHandlerWithLanguage(eventDate, 'type', currentLanguage)}
                                readonly={true}
                            />
                        </Col>
                        <Col xs={6}>
                            <TextField
                                name={fieldNameByLang("listenOn", currentLanguage)}
                                label={branding.configuration.eventDatesPage.sctEventDates.lblListenon}
                                placeholder={branding.configuration.eventDatesPage.sctEventDates.hntListenon}
                                value={mapListenOn(eventDate.isListable.get(), branding)}
                                onChange={() => { }}
                                readonly={true}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextField
                                name={fieldNameByLang("part", currentLanguage)}
                                label={branding.configuration.eventDatesPage.sctEventDates.lblPartEventSeries}
                                placeholder={branding.configuration.eventDatesPage.sctEventDates.hntPartEventSeries}
                                value=""
                                onChange={() => { }}
                                readonly={true}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <ToggleSwitchField
                                name={fieldNameByLang("breakout", currentLanguage)}
                                label={branding.configuration.eventDatesPage.sctEventDates.lblBreakoutRoom}
                                onChange={makeCheckHandler(eventDate, "breakoutAccess")}
                                value={getElementFieldValue(eventDate, "breakoutAccess")}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row>
                <Col>
                    <TextAreaField
                        name={fieldNameByLang("description", currentLanguage)}
                        label={branding.configuration.eventDatesPage.sctEventDates.lblDescription}
                        placeholder={branding.configuration.eventDatesPage.sctEventDates.hntDescription}
                        limit={4000}
                        value={getElementFieldValueWithLanguage(eventDate, "description", currentLanguage)}
                        onChange={makeSetHandlerWithLanguage(eventDate, 'description', currentLanguage)}
                        style={{ height: '100px' }}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <TextAreaField
                        name={fieldNameByLang("teaser", currentLanguage)}
                        label={branding.configuration.eventDatesPage.sctEventDates.lblSubtitle}
                        placeholder={branding.configuration.eventDatesPage.sctEventDates.hntSubtitle}
                        limit={300}
                        value={getElementFieldValueWithLanguage(eventDate, "teaser", currentLanguage)}
                        onChange={makeSetHandlerWithLanguage(eventDate, 'teaser', currentLanguage)}
                        style={{ height: '80px' }}
                    />
                </Col>
            </Row>
            {/*TODO get rid of these */}
            <br />
            <br />
            <Row>
                <Col xs={12}>
                    <Tabs>
                        {eventDate.type.get() === EVENT_DATE_TYPE_PRERECORDED &&
                            <Tab eventKey="prerecorded" title={branding.configuration.eventDatesPage.sctEventDates.lblTabPrerecorded}>
                                <PageContents description={branding.configuration.eventDatesPage.sctEventDates.lblPrerecordedDescription}>
                                    <PrerecordedContents eventDate={eventDate} />
                                </PageContents>
                            </Tab>
                        }
                        <Tab eventKey="speakers" title={branding.configuration.eventDatesPage.sctEventDates.lblTabSpeakers} tabClassName={hasErrorsAll(eventDate.persons) ? "invalid" : ""}>
                            <PageContents description={branding.configuration.eventDatesPage.sctEventDates.lblSpeakersDescription}>
                                <PersonsSection personsStates={eventDate.persons} />
                            </PageContents>
                        </Tab>
                        <Tab eventKey="multimedia" title={branding.configuration.eventDatesPage.sctEventDates.lblTabMultimedia} tabClassName={hasErrorsAll(eventDate.medias) ? "invalid" : ""}>
                            <PageContents description={branding.configuration.eventDatesPage.sctEventDates.lblMultimediaDescription}>
                                <MultimediaContents mediaStates={eventDate.medias} index={index} multimediaLimit={branding.configuration.eventDatesPage.sctEventDates.multimediaLimit} />
                            </PageContents>
                        </Tab>
                        {keywordsVisible &&
                            <Tab eventKey="keywords" title={branding.configuration.eventDatesPage.sctEventDates.lblTabKeywords}>
                                <PageContents description="">
                                    <KeywordContents value={eventDate.tags.get()} onChange={makeSetHandlerPlain(eventDate, 'tags')} />
                                </PageContents>
                            </Tab>
                        }
                    </Tabs>
                </Col>
            </Row>
        </Section>
    );
}

export default React.memo(withReferenceDataState(EventDatesSection))
